/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package cmp.navigation.rootnav

import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavDestination
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.navOptions
import cmp.navigation.authenticated.AuthenticatedGraphRoute
import cmp.navigation.authenticated.authenticatedGraph
import cmp.navigation.authenticated.navigateToAuthenticatedGraph
import cmp.navigation.splash.SplashRoute
import cmp.navigation.splash.navigateToSplash
import cmp.navigation.splash.splashDestination
import cmp.navigation.ui.rememberKptNavController
import cmp.navigation.utils.toObjectNavigationRoute
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.ui.NonNullEnterTransitionProvider
import studio.oddpods.cauce.core.base.ui.NonNullExitTransitionProvider
import studio.oddpods.cauce.core.base.ui.RootTransitionProviders
import studio.oddpods.cauce.feature.auth.LandingRoute
import studio.oddpods.cauce.feature.auth.landingDestination
import studio.oddpods.cauce.feature.auth.navigateToLanding
import studio.oddpods.cauce.feature.auth.navigateToOtp
import studio.oddpods.cauce.feature.auth.otpDestination
import studio.oddpods.cauce.feature.onboarding.OnboardingGraphRoute
import studio.oddpods.cauce.feature.onboarding.navigateToOnboardingGraph
import studio.oddpods.cauce.feature.onboarding.onboardingGraph
import kotlin.concurrent.atomics.AtomicReference
import kotlin.concurrent.atomics.ExperimentalAtomicApi

@OptIn(ExperimentalAtomicApi::class)
@Suppress("LongMethod", "CyclomaticComplexMethod")
@Composable
fun RootNavScreen(
    modifier: Modifier = Modifier,
    viewModel: RootNavViewModel = koinViewModel(),
    navController: NavHostController = rememberKptNavController(name = "RootNavScreen"),
    onSplashScreenRemoved: () -> Unit = {},
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    val previousStateReference = remember { AtomicReference(state) }

    val isNotSplashScreen = state != RootNavState.Splash
    LaunchedEffect(isNotSplashScreen) {
        if (isNotSplashScreen) onSplashScreenRemoved()
    }

    Surface(modifier = modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        NavHost(
            navController = navController,
            startDestination = SplashRoute,
            enterTransition = { toEnterTransition()(this) },
            exitTransition = { toExitTransition()(this) },
            popEnterTransition = { toEnterTransition()(this) },
            popExitTransition = { toExitTransition()(this) },
        ) {
            splashDestination()
            landingDestination(
                onOtpSent = { email ->
                    navController.navigateToOtp(email = email)
                },
            )
            otpDestination(
                onAuthSuccess = {
                    // Session observer in RootNavViewModel handles routing.
                },
                onNavigateBack = {
                    navController.popBackStack()
                },
            )
            onboardingGraph(
                navController = navController,
                onCompleted = {
                    navController.navigateToAuthenticatedGraph(
                        navOptions = navOptions {
                            popUpTo(navController.graph.id) {
                                inclusive = false
                                saveState = false
                            }
                            launchSingleTop = true
                            restoreState = false
                        },
                    )
                },
            )
            authenticatedGraph(navController)
        }
    }

    val targetRoute = when (state) {
        RootNavState.Splash -> SplashRoute
        RootNavState.NotAuthenticated -> LandingRoute
        RootNavState.NeedsOnboarding -> OnboardingGraphRoute
        RootNavState.Authenticated -> AuthenticatedGraphRoute
    }
    val currentRoute = navController.currentDestination?.rootLevelRoute()

    // Don't navigate if we are already at the correct root. This notably happens during process
    // death. In this case, the NavHost already restores state, so we don't have to navigate.
    // However, if the route is correct but the underlying state is different, we should still
    // proceed in order to get a fresh version of that route.
    if (currentRoute == targetRoute.toObjectNavigationRoute() &&
        previousStateReference.load() == state
    ) {
        previousStateReference.store(state)
        return
    }
    previousStateReference.store(state)

    // In some scenarios on an emulator the Activity can leak when recreated
    // if we don't first clear focus anytime we change the root destination.
    ClearFocus()

    // When state changes, navigate to different root navigation state
    val rootNavOptions = navOptions {
        // When changing root navigation state, pop everything else off the back stack:
        popUpTo(navController.graph.id) {
            inclusive = false
            saveState = false
        }
        launchSingleTop = true
        restoreState = false
    }

    // Use a LaunchedEffect to ensure we don't navigate too soon when the app first opens. This
    // avoids a bug that first appeared in Compose Material3 1.2.0-rc01 that causes the initial
    // transition to appear corrupted.
    LaunchedEffect(state) {
        when (state) {
            RootNavState.Splash -> navController.navigateToSplash(rootNavOptions)
            RootNavState.NotAuthenticated -> navController.navigateToLanding(rootNavOptions)
            RootNavState.NeedsOnboarding -> navController.navigateToOnboardingGraph(rootNavOptions)
            RootNavState.Authenticated -> navController.navigateToAuthenticatedGraph(
                navOptions = rootNavOptions,
            )
        }
    }
}

private fun NavDestination?.rootLevelRoute(): String? = when {
    this == null -> null
    parent?.route == null -> route
    else -> parent.rootLevelRoute()
}

@Suppress("MaxLineLength")
private fun AnimatedContentTransitionScope<NavBackStackEntry>.toEnterTransition(): NonNullEnterTransitionProvider =
    when (targetState.destination.rootLevelRoute()) {
        SplashRoute.toObjectNavigationRoute() -> RootTransitionProviders.Enter.none
        else -> RootTransitionProviders.Enter.fadeIn
    }

@Suppress("MaxLineLength")
private fun AnimatedContentTransitionScope<NavBackStackEntry>.toExitTransition(): NonNullExitTransitionProvider {
    return when (initialState.destination.rootLevelRoute()) {
        // Disable transitions when coming from the splash screen
        SplashRoute.toObjectNavigationRoute() -> RootTransitionProviders.Exit.none
        else -> RootTransitionProviders.Exit.fadeOut
    }
}

@Composable
expect fun ClearFocus()
